import { route } from 'quasar/wrappers'
import { createMemoryHistory, createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import routes from './routes'
import { ACCOUNT_STATES, auth } from 'boot/auth'
import { watch } from 'vue'
import { empty } from 'src/utils/variables'

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : (process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory)

  const Router = createRouter({
    scrollBehavior: () => ({
      left: 0,
      top: 0
    }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.MODE === 'ssr' ? void 0 : process.env.VUE_ROUTER_BASE)
  })

  Router.beforeEach((to, from, next) => {
    if (!auth.loading.value) {
      next()
      return
    }

    const unwatch = watch(auth.loading, loading => {
      if (!loading) {
        unwatch()
        next()
      }
    })
  })

  Router.beforeEach((to, from, next) => {
    if (to.matched.some(route => route.meta && route.meta.error)) {
      next()
      return
    }

    const meta = Object.assign({}, ...to.matched.map(route => route.meta))
    const isRouteAnonymous = meta.anonymous || meta.anonymousOnly || false
    const isRouteAnonymousOnly = meta.anonymousOnly || false
    const accountState = auth.accountState.value

    if (accountState === ACCOUNT_STATES.MFA_REQUIRED && to.path !== '/security/mfa') {
      next('/security/mfa')
    } else if ([ACCOUNT_STATES.PASSWORD_EXPIRED, ACCOUNT_STATES.PASSWORD_CREATION_REQUIRED].includes(accountState) && to.path !== '/security/password-renewal') {
      next({ name: 'password-renewal' })
    } else if (accountState === ACCOUNT_STATES.EMAIL_VERIFICATION_REQUIRED && to.path !== '/security/verify-email') {
      next({
        name: 'verify-email',
        query: { email: auth.emailVerificationEmail.value }
      })
    } else if (accountState === ACCOUNT_STATES.MOBILE_PHONE_VERIFICATION_REQUIRED && to.path !== '/security/verify-mobile-phone') {
      next({
        name: 'verify-mobile-phone'
      })
    } else if (accountState === ACCOUNT_STATES.CGU_CONSENT_REQUIRED && to.path !== '/security/accept-cgu') {
      next('/security/accept-cgu')
    } else if (!isRouteAnonymous && !auth.user.value && to.path !== '/security/login') {
      next('/security/login')
    } else if (auth.isEnrollBiometry.value && auth.user.value && to.path !== '/security/biometrics') {
      next('/security/biometrics')
    } else if (!auth.isEnrollBiometry.value && isRouteAnonymousOnly && auth.user.value && to.path !== '/') {
      next('/')
    } else {
      next()
    }
  })

  Router.beforeEach((to, from, next) => {
    // If the new route (to) does not have its own parameters, then they will be taken from the previous route (from)
    // We cannot use argument from because all its props are null at the first navigation but redirectedFrom contains valid props.
    if (!empty(to.query)) {
      next()
      return
    }

    if (to.redirectedFrom && !empty(to.redirectedFrom.query)) {
      next(Object.assign({}, to, { query: to.redirectedFrom.query }))
      return
    }

    if (from && !empty(from.query)) {
      next(Object.assign({}, to, { query: from.query }))
      return
    }

    next()
  })

  Router.beforeEach((to, from, next) => {
    const meta = Object.assign({}, ...to.matched.map(route => route.meta))
    if (meta.admin && !auth.isAdmin.value) {
      next({ path: '/' })
    }

    next()
  })

  /**
   * Handle missing chunks after a deployment of new static files that replace previous ones
   * the current user might need and hasn't loaded yet.
   */
  const loadingChunkFailedErrorRegex = /loading .*?chunk .*?failed/i
  Router.onError((error, to/*, from */) => {
    if (loadingChunkFailedErrorRegex.test(error.message)) {
      window.location.assign(to.fullPath)
    }
  })

  return Router
})
